/**
 * Email Cache Management Service
 * High-level API for caching emails with IndexedDB + in-memory cache
 * Implements hybrid caching strategy for optimal performance
 */
import { emailDB } from './emailDB';
// =====================================================
// EMAIL CACHE SERVICE CLASS
// =====================================================
class EmailCacheService {
    // In-memory cache for hot data (Layer 3)
    memoryCache = new Map();
    // Cache statistics
    stats = {
        hit: 0,
        miss: 0,
        hitRate: 0,
    };
    // Default TTL for in-memory cache: 5 minutes
    DEFAULT_TTL = 5 * 60 * 1000;
    // Sync callbacks
    syncCallbacks = new Map();
    // =====================================================
    // CACHE OPERATIONS
    // =====================================================
    /**
     * Get emails for a folder with hybrid caching
     * 1. Check in-memory cache first (fastest)
     * 2. Check IndexedDB (fast)
     * 3. Return null if not cached (caller should fetch from API)
     */
    async getEmails(folder, useMemoryCache = true) {
        const cacheKey = `emails:${folder}`;
        // Layer 3: Check in-memory cache first
        if (useMemoryCache) {
            const memEntry = this.memoryCache.get(cacheKey);
            if (memEntry && Date.now() - memEntry.timestamp < memEntry.ttl) {
                this.stats.hit++;
                this.updateHitRate();
                console.log(`[Cache] Memory HIT for ${folder}`);
                return memEntry.data;
            }
        }
        // Layer 2: Check IndexedDB
        try {
            const emails = await emailDB.getEmailsByFolderSorted(folder);
            if (emails.length > 0) {
                // Store in memory cache for next time
                this.setMemoryCache(cacheKey, emails, this.DEFAULT_TTL);
                this.stats.hit++;
                this.updateHitRate();
                console.log(`[Cache] IndexedDB HIT for ${folder} (${emails.length} emails)`);
                return emails;
            }
        }
        catch (error) {
            console.error('[Cache] IndexedDB read error:', error);
        }
        // Cache miss - caller should fetch from API
        this.stats.miss++;
        this.updateHitRate();
        console.log(`[Cache] MISS for ${folder}`);
        return null;
    }
    /**
     * Store emails in both IndexedDB and memory cache
     */
    async setEmails(folder, emails) {
        const cacheKey = `emails:${folder}`;
        try {
            // Store in IndexedDB (persistent)
            await emailDB.putEmails(emails);
            // Store in memory cache (fast access)
            this.setMemoryCache(cacheKey, emails, this.DEFAULT_TTL);
            // Update folder counts
            await emailDB.updateFolderCounts(folder);
            console.log(`[Cache] Stored ${emails.length} emails for ${folder}`);
        }
        catch (error) {
            console.error('[Cache] Failed to store emails:', error);
            throw error;
        }
    }
    /**
     * Update a single email (e.g., mark as read, star, etc.)
     */
    async updateEmail(id, updates) {
        try {
            // Update in IndexedDB
            await emailDB.updateEmail(id, updates);
            // Invalidate memory cache for affected folder
            const email = await emailDB.getEmail(id);
            if (email) {
                this.invalidateMemoryCache(`emails:${email.folder}`);
                // Update folder counts if read status changed
                if ('isRead' in updates) {
                    await emailDB.updateFolderCounts(email.folder);
                }
            }
            console.log(`[Cache] Updated email ${id}`);
        }
        catch (error) {
            console.error('[Cache] Failed to update email:', error);
            throw error;
        }
    }
    /**
     * Delete an email
     */
    async deleteEmail(id) {
        try {
            const email = await emailDB.getEmail(id);
            if (email) {
                await emailDB.deleteEmail(id);
                this.invalidateMemoryCache(`emails:${email.folder}`);
                await emailDB.updateFolderCounts(email.folder);
                console.log(`[Cache] Deleted email ${id}`);
            }
        }
        catch (error) {
            console.error('[Cache] Failed to delete email:', error);
            throw error;
        }
    }
    /**
     * Get a single email by ID
     */
    async getEmail(id) {
        try {
            const email = await emailDB.getEmail(id);
            return email || null;
        }
        catch (error) {
            console.error('[Cache] Failed to get email:', error);
            return null;
        }
    }
    /**
     * Search emails across all folders or specific folder
     */
    async searchEmails(query, folder) {
        try {
            return await emailDB.searchEmails(query, folder);
        }
        catch (error) {
            console.error('[Cache] Search failed:', error);
            return [];
        }
    }
    /**
     * Get emails by thread ID
     */
    async getEmailsByThread(threadId) {
        try {
            return await emailDB.getEmailsByThread(threadId);
        }
        catch (error) {
            console.error('[Cache] Failed to get thread:', error);
            return [];
        }
    }
    // =====================================================
    // FOLDER OPERATIONS
    // =====================================================
    /**
     * Get folder information
     */
    async getFolder(folderId) {
        const cacheKey = `folder:${folderId}`;
        // Check memory cache
        const memEntry = this.memoryCache.get(cacheKey);
        if (memEntry && Date.now() - memEntry.timestamp < memEntry.ttl) {
            return memEntry.data;
        }
        // Get from IndexedDB
        try {
            const folder = await emailDB.getFolder(folderId);
            if (folder) {
                this.setMemoryCache(cacheKey, folder, this.DEFAULT_TTL);
                return folder;
            }
        }
        catch (error) {
            console.error('[Cache] Failed to get folder:', error);
        }
        return null;
    }
    /**
     * Get all folders
     */
    async getAllFolders() {
        const cacheKey = 'folders:all';
        // Check memory cache
        const memEntry = this.memoryCache.get(cacheKey);
        if (memEntry && Date.now() - memEntry.timestamp < memEntry.ttl) {
            return memEntry.data;
        }
        // Get from IndexedDB
        try {
            const folders = await emailDB.getAllFolders();
            this.setMemoryCache(cacheKey, folders, this.DEFAULT_TTL);
            return folders;
        }
        catch (error) {
            console.error('[Cache] Failed to get folders:', error);
            return [];
        }
    }
    /**
     * Set folder information
     */
    async setFolder(folder) {
        try {
            await emailDB.putFolder(folder);
            this.invalidateMemoryCache(`folder:${folder.id}`);
            this.invalidateMemoryCache('folders:all');
        }
        catch (error) {
            console.error('[Cache] Failed to set folder:', error);
            throw error;
        }
    }
    // =====================================================
    // SYNC OPERATIONS
    // =====================================================
    /**
     * Get sync state for a folder
     */
    async getSyncState(folder) {
        try {
            const state = await emailDB.getSyncState(folder);
            return state || null;
        }
        catch (error) {
            console.error('[Cache] Failed to get sync state:', error);
            return null;
        }
    }
    /**
     * Update sync progress
     */
    async updateSyncProgress(folder, syncedEmails, totalEmails) {
        try {
            await emailDB.updateSyncProgress(folder, syncedEmails, totalEmails);
            // Notify listeners
            const progress = {
                folder,
                synced: syncedEmails,
                total: totalEmails,
                percentage: totalEmails > 0 ? (syncedEmails / totalEmails) * 100 : 0,
                isComplete: syncedEmails >= totalEmails,
                isSyncing: syncedEmails < totalEmails,
            };
            this.notifySyncCallbacks(folder, progress);
        }
        catch (error) {
            console.error('[Cache] Failed to update sync progress:', error);
        }
    }
    /**
     * Subscribe to sync progress updates
     */
    onSyncProgress(folder, callback) {
        if (!this.syncCallbacks.has(folder)) {
            this.syncCallbacks.set(folder, []);
        }
        this.syncCallbacks.get(folder).push(callback);
        // Return unsubscribe function
        return () => {
            const callbacks = this.syncCallbacks.get(folder);
            if (callbacks) {
                const index = callbacks.indexOf(callback);
                if (index > -1) {
                    callbacks.splice(index, 1);
                }
            }
        };
    }
    /**
     * Notify all sync callbacks
     */
    notifySyncCallbacks(folder, progress) {
        const callbacks = this.syncCallbacks.get(folder);
        if (callbacks) {
            callbacks.forEach(callback => {
                try {
                    callback(progress);
                }
                catch (error) {
                    console.error('[Cache] Sync callback error:', error);
                }
            });
        }
    }
    // =====================================================
    // MEMORY CACHE OPERATIONS
    // =====================================================
    /**
     * Set data in memory cache
     */
    setMemoryCache(key, data, ttl = this.DEFAULT_TTL) {
        this.memoryCache.set(key, {
            data,
            timestamp: Date.now(),
            ttl,
        });
    }
    /**
     * Invalidate a specific memory cache entry
     */
    invalidateMemoryCache(key) {
        this.memoryCache.delete(key);
        console.log(`[Cache] Invalidated memory cache: ${key}`);
    }
    /**
     * Invalidate all memory cache entries for a folder
     */
    invalidateFolderCache(folder) {
        this.invalidateMemoryCache(`emails:${folder}`);
        this.invalidateMemoryCache(`folder:${folder}`);
    }
    /**
     * Clear all memory cache
     */
    clearMemoryCache() {
        this.memoryCache.clear();
        console.log('[Cache] Cleared all memory cache');
    }
    /**
     * Clean up expired memory cache entries
     */
    cleanupMemoryCache() {
        const now = Date.now();
        let cleaned = 0;
        for (const [key, entry] of this.memoryCache.entries()) {
            if (now - entry.timestamp >= entry.ttl) {
                this.memoryCache.delete(key);
                cleaned++;
            }
        }
        if (cleaned > 0) {
            console.log(`[Cache] Cleaned up ${cleaned} expired memory cache entries`);
        }
    }
    // =====================================================
    // CACHE MANAGEMENT
    // =====================================================
    /**
     * Force refresh emails for a folder (fetch from API and update cache)
     */
    async refreshFolder(folder) {
        this.invalidateFolderCache(folder);
        console.log(`[Cache] Forced refresh for ${folder}`);
    }
    /**
     * Clear all cached emails
     */
    async clearAllEmails() {
        try {
            await emailDB.clearAllEmails();
            this.clearMemoryCache();
            console.log('[Cache] Cleared all cached emails');
        }
        catch (error) {
            console.error('[Cache] Failed to clear emails:', error);
            throw error;
        }
    }
    /**
     * Clear all cache data (complete reset)
     */
    async clearAll() {
        try {
            await emailDB.clearAll();
            this.clearMemoryCache();
            this.stats = { hit: 0, miss: 0, hitRate: 0 };
            console.log('[Cache] Complete cache reset');
        }
        catch (error) {
            console.error('[Cache] Failed to clear cache:', error);
            throw error;
        }
    }
    // =====================================================
    // STATISTICS
    // =====================================================
    /**
     * Get cache statistics
     */
    getCacheStats() {
        return { ...this.stats };
    }
    /**
     * Get detailed database statistics
     */
    async getDetailedStats() {
        try {
            const dbStats = await emailDB.getStats();
            return {
                ...dbStats,
                memoryCache: {
                    entries: this.memoryCache.size,
                    hitRate: this.stats.hitRate,
                },
                cache: this.stats,
            };
        }
        catch (error) {
            console.error('[Cache] Failed to get stats:', error);
            return null;
        }
    }
    /**
     * Update hit rate calculation
     */
    updateHitRate() {
        const total = this.stats.hit + this.stats.miss;
        this.stats.hitRate = total > 0 ? (this.stats.hit / total) * 100 : 0;
    }
    /**
     * Reset statistics
     */
    resetStats() {
        this.stats = { hit: 0, miss: 0, hitRate: 0 };
    }
    // =====================================================
    // UTILITY
    // =====================================================
    /**
     * Initialize cache service (called on app startup)
     */
    async initialize() {
        try {
            await emailDB.init();
            console.log('[Cache] Email cache service initialized');
            // Start periodic cleanup of memory cache (every 5 minutes)
            setInterval(() => {
                this.cleanupMemoryCache();
            }, 5 * 60 * 1000);
        }
        catch (error) {
            console.error('[Cache] Failed to initialize:', error);
            throw error;
        }
    }
    /**
     * Check if cache is working (health check)
     */
    async healthCheck() {
        try {
            // Try to get database stats
            await emailDB.getStats();
            return true;
        }
        catch (error) {
            console.error('[Cache] Health check failed:', error);
            return false;
        }
    }
}
// =====================================================
// SINGLETON INSTANCE
// =====================================================
export const emailCache = new EmailCacheService();
// Auto-initialize on import
emailCache.initialize().catch(err => {
    console.error('Failed to auto-initialize email cache service:', err);
});
